import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight, Menu, X, MoreVertical, Video, Instagram, Facebook, Linkedin } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_dynamic-conversion/artifacts/uw2iwdym_LOGO%202.1%20X%201%20in.png";

const NAV_LINKS = [
  { label: "Home", href: "/#home", testId: "nav-link-home" },
  { label: "Services", href: "/#services", testId: "nav-link-services" },
  { label: "Methodology", href: "/#methodology", testId: "nav-link-methodology" },
  { label: "Case Studies", href: "/#case-studies", testId: "nav-link-case-studies" },
  { label: "Contact", href: "mailto:Connect@dynamicxmedia.com", testId: "nav-link-contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/75 backdrop-blur-xl border-b border-black/10 shadow-[0_4px_24px_-12px_rgba(0,0,0,0.08)]"
          : "bg-transparent backdrop-blur-0 border-b border-transparent"
      }`}
      style={{ top: "34px" }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between">
        {/* Logo */}
        <Link
          to="/"
          data-testid="site-logo"
          className="flex items-center group"
          style={{ marginLeft: "16px" }}
        >
          <img
            src={LOGO_URL}
            alt="Dynamic X Media logo — Real Estate Digital Strategist & Branding Expert (Results that Resonate)"
            className="block transition-transform duration-300 group-hover:scale-[1.03]"
            style={{
              height: "44px",
              width: "auto",
              objectFit: "contain",
            }}
          />
        </Link>

        {/* Nav links */}
        <nav className="hidden lg:flex items-center gap-8 absolute left-1/2 -translate-x-1/2">
          {NAV_LINKS.map((l) => (
            <a
              key={l.label}
              href={l.href}
              data-testid={l.testId}
              className="relative text-sm font-medium text-black/80 hover:text-[#2770FD] transition-colors after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-[#2770FD] hover:after:w-full after:transition-all after:duration-300"
            >
              {l.label}
            </a>
          ))}
        </nav>

        {/* Right cluster — CTA + 3-dot more menu + mobile toggle */}
        <div className="flex items-center gap-3">
          <a
            href="https://wa.me/918602265191?text=Hey%20Dynamic%20X%20Media%2C%20I%20want%20to%20claim%20my%20Free%20Marketing%20%26%20Branding%20Audit.%20Here%20are%20my%20details%3A%0A%0A-%20Name%3A%20%0A-%20Email%3A%20%0A-%20Company%3A%20%0A-%20Designation%3A"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-cta-button"
            className="hidden sm:inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-cta-solid text-white text-sm font-semibold tracking-wide glow-cta-light transition-all duration-300 hover:-translate-y-0.5"
          >
            Get an Audit
            <ArrowUpRight className="h-4 w-4" />
          </a>

          {/* 3-dot menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="nav-more-menu"
                aria-label="More navigation"
                className="hidden lg:inline-flex h-10 w-10 items-center justify-center rounded-full border border-black/10 bg-white/60 backdrop-blur text-black hover:text-[#2770FD] hover:border-[#2770FD] transition-all duration-300"
              >
                <MoreVertical className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              sideOffset={12}
              data-testid="nav-more-dropdown"
              className="w-60 bg-white border-black/10 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.25)]"
            >
              <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.25em] text-black/55 font-semibold">
                More from Dynamic X
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-black/10" />
              <DropdownMenuItem asChild>
                <Link
                  to="/testimonials"
                  data-testid="nav-link-video-testimonials"
                  className="flex items-center gap-3 cursor-pointer text-black hover:!text-[#2770FD] hover:!bg-[#2770FD]/5 focus:!bg-[#2770FD]/5 focus:!text-[#2770FD] py-2.5"
                >
                  <Video className="h-4 w-4 text-[#2770FD]" />
                  <div className="flex flex-col">
                    <span className="text-sm font-semibold">Video Testimonials</span>
                    <span className="text-[11px] text-black/55">Client Success Hub</span>
                  </div>
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <a
                  href="mailto:Connect@dynamicxmedia.com?subject=Partnership%20Enquiry"
                  data-testid="nav-link-partner"
                  className="flex items-center gap-3 cursor-pointer text-black hover:!text-[#2770FD] hover:!bg-[#2770FD]/5 focus:!bg-[#2770FD]/5 focus:!text-[#2770FD] py-2.5"
                >
                  <ArrowUpRight className="h-4 w-4 text-[#2770FD]" />
                  <div className="flex flex-col">
                    <span className="text-sm font-semibold">Partner with us</span>
                    <span className="text-[11px] text-black/55">Business enquiries</span>
                  </div>
                </a>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-md border border-black/15 text-black hover:bg-black/5"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden border-t border-black/10 bg-white"
        >
          <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-4">
            {NAV_LINKS.map((l) => (
              <a
                key={l.label}
                href={l.href}
                data-testid={`mobile-${l.testId}`}
                onClick={() => setOpen(false)}
                className="text-base font-medium text-black hover:text-[#2770FD]"
              >
                {l.label}
              </a>
            ))}
            <Link
              to="/testimonials"
              data-testid="mobile-nav-link-video-testimonials"
              onClick={() => setOpen(false)}
              className="text-base font-medium text-[#2770FD] inline-flex items-center gap-2"
            >
              <Video className="h-4 w-4" /> Video Testimonials
            </Link>

            {/* Social icons row inside drawer — directly under Video Testimonials */}
            <div
              data-testid="mobile-socials"
              className="flex items-center gap-3 pt-1"
            >
              <a
                href="https://www.instagram.com/dynamicxmedia"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                data-testid="mobile-social-instagram"
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-black/15 text-black hover:text-white hover:bg-[#2770FD] hover:border-[#2770FD] transition-colors"
              >
                <Instagram className="h-4 w-4" strokeWidth={1.75} />
              </a>
              <a
                href="https://www.facebook.com/dynamicxmedia"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                data-testid="mobile-social-facebook"
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-black/15 text-black hover:text-white hover:bg-[#2770FD] hover:border-[#2770FD] transition-colors"
              >
                <Facebook className="h-4 w-4" strokeWidth={1.75} />
              </a>
              <a
                href="https://www.linkedin.com/company/dynamicxmedia"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                data-testid="mobile-social-linkedin"
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-black/15 text-black hover:text-white hover:bg-[#2770FD] hover:border-[#2770FD] transition-colors"
              >
                <Linkedin className="h-4 w-4" strokeWidth={1.75} />
              </a>
            </div>

            <a
              href="https://wa.me/918602265191?text=Hey%20Dynamic%20X%20Media%2C%20I%20want%20to%20claim%20my%20Free%20Marketing%20%26%20Branding%20Audit.%20Here%20are%20my%20details%3A%0A%0A-%20Name%3A%20%0A-%20Email%3A%20%0A-%20Company%3A%20%0A-%20Designation%3A"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-header-cta-button"
              className="mt-2 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-cta-solid text-white text-sm font-semibold"
            >
              Get an Audit <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
